import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconBolt,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { FilterBuilder, type AndTree } from '../../components/FilterBuilder';

function AutoLabelsPage() {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const list = trpc.contacts.autoLabels.list.useQuery();
  const labels = trpc.contacts.labels.list.useQuery();

  const [open, h] = useDisclosure(false);
  const [editing, setEditing] = useState<{
    ruleId?: number;
    name: string;
    labelId: number | null;
    isActive: boolean;
    condition: AndTree | null;
  }>({ name: '', labelId: null, isActive: true, condition: null });

  const upsert = trpc.contacts.autoLabels.upsert.useMutation({
    onSuccess: () => {
      void utils.contacts.autoLabels.list.invalidate();
      h.close();
      notifications.show({ message: 'Rule saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const setActive = trpc.contacts.autoLabels.setActive.useMutation({
    onSuccess: () => void utils.contacts.autoLabels.list.invalidate(),
  });
  const remove = trpc.contacts.autoLabels.delete.useMutation({
    onSuccess: () => {
      void utils.contacts.autoLabels.list.invalidate();
      notifications.show({ message: 'Rule deleted', color: 'green' });
    },
  });

  const labelLookup = new Map((labels.data ?? []).map((l) => [l.id, l.title]));

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate({ to: '/vendor/contacts', search: { tab: 'labels' } })}
          >
            Labels
          </Button>
        </Group>
        <Group justify="space-between">
          <Title order={2}>Auto-labels</Title>
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={() => {
              setEditing({ name: '', labelId: null, isActive: true, condition: null });
              h.open();
            }}
          >
            New rule
          </Button>
        </Group>

        <Card>
          <Group gap="xs">
            <IconBolt size={14} />
            <Text size="sm" c="dimmed">
              Rules are evaluated nightly at 01:15 (after engagement scores recompute). Matching
              contacts get the rule's label additively — pre-existing label assignments are
              preserved.
            </Text>
          </Group>
        </Card>

        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={200} m="md" />
          ) : (list.data ?? []).length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              No rules yet — create one to auto-label contacts as they evolve.
            </Text>
          ) : (
            <Table striped withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Rule</Table.Th>
                  <Table.Th>Label</Table.Th>
                  <Table.Th>Last run</Table.Th>
                  <Table.Th>Matched</Table.Th>
                  <Table.Th>Active</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text fw={500} size="sm">
                        {r.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">{labelLookup.get(r.labelId) ?? `#${r.labelId}`}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {r.lastEvaluatedAt
                          ? new Date(r.lastEvaluatedAt).toLocaleString()
                          : 'pending first tick'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.matchedCount}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        size="sm"
                        checked={r.isActive}
                        onChange={(e) =>
                          setActive.mutate({
                            ruleId: r.id,
                            isActive: e.currentTarget.checked,
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap={4} justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => {
                            try {
                              const tree = JSON.parse(r.conditionJson) as AndTree;
                              setEditing({
                                ruleId: r.id,
                                name: r.name,
                                labelId: r.labelId,
                                isActive: r.isActive,
                                condition: tree,
                              });
                              h.open();
                            } catch {
                              setEditing({
                                ruleId: r.id,
                                name: r.name,
                                labelId: r.labelId,
                                isActive: r.isActive,
                                condition: null,
                              });
                              h.open();
                            }
                          }}
                        >
                          {r.isActive ? (
                            <IconPlayerPause size={14} />
                          ) : (
                            <IconPlayerPlay size={14} />
                          )}
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => remove.mutate({ ruleId: r.id })}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={open}
        onClose={h.close}
        title={editing.ruleId ? 'Edit auto-label rule' : 'New auto-label rule'}
        size="lg"
        centered
      >
        <Stack>
          <TextInput
            label="Rule name"
            required
            value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.currentTarget.value })}
          />
          <Select
            label="Apply label"
            required
            data={(labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }))}
            value={editing.labelId ? String(editing.labelId) : null}
            onChange={(v) => setEditing({ ...editing, labelId: v ? Number(v) : null })}
          />
          <Switch
            label="Active"
            checked={editing.isActive}
            onChange={(e) => setEditing({ ...editing, isActive: e.currentTarget.checked })}
          />
          <Box>
            <Text fw={500} size="sm" mb="xs">
              When all of these conditions match
            </Text>
            <FilterBuilder
              value={editing.condition}
              onChange={(t) => setEditing({ ...editing, condition: t })}
            />
          </Box>
          <Group justify="end">
            <Button variant="default" onClick={h.close}>
              Cancel
            </Button>
            <Button
              loading={upsert.isPending}
              disabled={
                !editing.name.trim() ||
                !editing.labelId ||
                !editing.condition ||
                editing.condition.terms.length === 0
              }
              onClick={() =>
                upsert.mutate({
                  ruleId: editing.ruleId,
                  name: editing.name,
                  labelId: editing.labelId!,
                  isActive: editing.isActive,
                  condition: editing.condition!,
                })
              }
            >
              Save rule
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/contacts/auto-labels')({
  component: AutoLabelsPage,
});
